import { get, post, put, del } from '@/services/apiClient';
import type {
  IWorkflow,
  IExecution,
  IExecutionResult,
  IDriveFile,
  ITemplate,
  ICreateWorkflowRequest,
  IUpdateWorkflowRequest,
  IApprovedResult,
} from '@/types/docflow';

const API_BASE = '/api/v1/docflow';

// ============================================================================
// Workflows
// ============================================================================

export async function listWorkflows(): Promise<IWorkflow[]> {
  return get<IWorkflow[]>(`${API_BASE}/workflows`);
}

export async function getWorkflow(id: string): Promise<IWorkflow> {
  return get<IWorkflow>(`${API_BASE}/workflows/${id}`);
}

export async function createWorkflow(data: ICreateWorkflowRequest): Promise<IWorkflow> {
  return post<IWorkflow>(`${API_BASE}/workflows`, data);
}

export async function updateWorkflow(id: string, data: IUpdateWorkflowRequest): Promise<IWorkflow> {
  return put<IWorkflow>(`${API_BASE}/workflows/${id}`, data);
}

export async function deleteWorkflow(id: string): Promise<void> {
  return del(`${API_BASE}/workflows/${id}`);
}

// ============================================================================
// Executions
// ============================================================================

export async function runWorkflow(id: string, fileIds: string[]): Promise<IExecution> {
  return post<IExecution>(`${API_BASE}/workflows/${id}/run`, { file_ids: fileIds });
}

export async function extractWorkflow(id: string, fileIds: string[]): Promise<IExecution> {
  return post<IExecution>(`${API_BASE}/workflows/${id}/extract`, { file_ids: fileIds });
}

export async function approveExecution(id: string, results: IApprovedResult[]): Promise<IExecution> {
  return post<IExecution>(`${API_BASE}/executions/${id}/approve`, { results });
}

export async function deleteExecution(id: string): Promise<void> {
  return del(`${API_BASE}/executions/${id}`);
}

export async function listExecutions(workflowId: string): Promise<IExecution[]> {
  return get<IExecution[]>(`${API_BASE}/workflows/${workflowId}/executions`);
}

export async function getExecution(id: string): Promise<IExecution> {
  return get<IExecution>(`${API_BASE}/executions/${id}`);
}

export async function getExecutionResults(executionId: string): Promise<IExecutionResult[]> {
  return get<IExecutionResult[]>(`${API_BASE}/executions/${executionId}/results`);
}

// ============================================================================
// Drive browsing
// ============================================================================

export async function listDriveSubFolders(folderId: string): Promise<IDriveFile[]> {
  return get<IDriveFile[]>(`${API_BASE}/drive/folders/${folderId}/folders`);
}

export async function listDriveFiles(folderId: string): Promise<IDriveFile[]> {
  return get<IDriveFile[]>(`${API_BASE}/drive/folders/${folderId}/files`);
}

// ============================================================================
// File content proxy
// ============================================================================

export function getFileContentUrl(fileId: string): string {
  return `${API_BASE}/drive/files/${fileId}/content`;
}

// ============================================================================
// Templates
// ============================================================================

export async function listTemplates(): Promise<ITemplate[]> {
  return get<ITemplate[]>(`${API_BASE}/templates`);
}
